/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { AlertResponse } from '@/client';
import { Aside } from '@/components/Aside';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Col } from '@/primitives/Col';
import { Row } from '@/primitives/Row';
import { Text } from '@/primitives/Text';

interface AlertResponseExpandedRowProps {
  response: AlertResponse;
}

export function AlertResponseExpandedRow({ response }: AlertResponseExpandedRowProps) {
  const { t } = useMimicTranslation('notificationManagement');

  return (
    <Aside gap="sm">
      <Row>
        <Col span={4}>
          <Text strong>{t('responses.statusCode')}</Text>
        </Col>
        <Col span={20}>
          <Text>{response.statusCode || 'N/A'}</Text>
        </Col>
      </Row>
      <Row>
        <Col span={4}>
          <Text strong>{t('responses.error')}</Text>
        </Col>
        <Col span={20}>
          <Text>{response.error ? t('responses.yes') : t('responses.no')}</Text>
        </Col>
      </Row>
      {response.errorMessage && (
        <Row>
          <Col span={4}>
            <Text strong>{t('responses.errorMessage')}</Text>
          </Col>
          <Col span={20}>
            <Text type="error">{response.errorMessage}</Text>
          </Col>
        </Row>
      )}
      {response.responseBody && (
        <Row>
          <Col span={4}>
            <Text strong>{t('responses.responseBody')}</Text>
          </Col>
          <Col span={20}>
            <pre
              style={{
                fontSize: '12px',
                fontFamily: 'monospace',
                backgroundColor: '#f5f5f5',
                padding: '8px',
                borderRadius: '4px',
                maxHeight: '300px',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {response.responseBody}
            </pre>
          </Col>
        </Row>
      )}
      <Row>
        <Col span={4}>
          <Text strong>{t('responses.telemetryId')}</Text>
        </Col>
        <Col span={20}>
          <Text type="subtle" size="sm">
            {response.telemetryId}
          </Text>
        </Col>
      </Row>
    </Aside>
  );
}
